import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Button,
  Box,
  CircularProgress,
  Breadcrumbs,
  Link as MuiLink,
  Alert,
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore'; // For 'Up' button
import HomeIcon from '@mui/icons-material/Home'; // For root breadcrumb
import axios from 'axios';

const FolderBrowserDialog = ({
  open,
  onClose,
  onSelectPath,
  selectedLibrary,
  initialPath,
  configId,
  getToken,
  userEmail,
}) => {
  const [currentPath, setCurrentPath] = useState('/');
  const [folders, setFolders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [breadcrumbs, setBreadcrumbs] = useState([{ name: 'Root', path: '/' }]);

  const buildBreadcrumbs = (path) => {
    const segments = path.split('/').filter(Boolean);
    const newBreadcrumbs = [{ name: 'Root', path: '/' }];
    let currentBuildPath = '/';
    for (const segment of segments) {
      currentBuildPath += (currentBuildPath === '/' ? '' : '/') + segment;
      newBreadcrumbs.push({ name: segment, path: currentBuildPath });
    }
    return newBreadcrumbs;
  };

  const fetchFolders = useCallback(async (path) => {
    if (!selectedLibrary || !configId || !getToken || !userEmail) {
      setError('Missing required parameters to fetch folders.');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const token = getToken();
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/sharepoint/libraries/${selectedLibrary.library_id}/folders`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: { path: path === '/' ? '' : path, configId }, // API expects empty string for root
        }
      );
      if (response.data && response.data.success) {
        setFolders(response.data.folders || []);
        setCurrentPath(path);
        setBreadcrumbs(buildBreadcrumbs(path));
      } else {
        setError(response.data?.message || 'Failed to fetch folders.');
        setFolders([]);
      }
    } catch (err) {
      console.error('Error fetching folders:', err);
      setError(err.response?.data?.message || err.message || 'An error occurred while fetching folders.');
      setFolders([]);
    } finally {
      setIsLoading(false);
    }
  }, [selectedLibrary, configId, getToken, userEmail]);

  useEffect(() => {
    if (open && selectedLibrary) {
      const pathToGo = initialPath && initialPath !== '/' ? initialPath : '/';
      fetchFolders(pathToGo);
    } else if (!open) {
      // Reset when dialog closes
      setCurrentPath('/');
      setFolders([]);
      setError(null);
      setBreadcrumbs([{ name: 'Root', path: '/' }]);
    }
  }, [open, selectedLibrary, initialPath, fetchFolders]);

  const handleFolderClick = (folder) => {
    // The 'path' from API is usually the full path including the folder name itself.
    // Or, if we construct it, ensure it's the path *to* the folder's contents.
    fetchFolders(folder.path);
  };
  
  const handleBreadcrumbClick = (path) => {
    fetchFolders(path);
  };

  const handleUpLevel = () => {
    if (currentPath === '/') return;
    const segments = currentPath.split('/').filter(Boolean);
    segments.pop();
    const parentPath = segments.length > 0 ? '/' + segments.join('/') : '/';
    fetchFolders(parentPath);
  };

  const handleSelect = () => {
    onSelectPath(currentPath);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        Browse Folders in {selectedLibrary?.library_name || 'Library'}
        <Typography variant="caption" display="block">Current path: {currentPath}</Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ mb: 2 }}>
          <Breadcrumbs aria-label="breadcrumb">
            {breadcrumbs.map((crumb, index) => (
              <MuiLink
                component="button"
                key={index}
                onClick={() => handleBreadcrumbClick(crumb.path)}
                sx={{ cursor: 'pointer', typography: 'body2' }}
              >
                {index === 0 && <HomeIcon sx={{ mr: 0.5, verticalAlign: 'bottom' }} fontSize="inherit" />}
                {crumb.name}
              </MuiLink>
            ))}
          </Breadcrumbs>
        </Box>

        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Loading folders...</Typography>
          </Box>
        )}
        {!isLoading && error && (
          <Alert severity="error" sx={{ mt: 1, mb: 1 }}>{error}</Alert>
        )}
        {!isLoading && !error && folders.length === 0 && currentPath !== '/' && (
           <Alert severity="info">No subfolders found in this folder.</Alert>
        )}
         {!isLoading && !error && folders.length === 0 && currentPath === '/' && (
           <Alert severity="info">This library has no folders at the root level.</Alert>
        )}

        {!isLoading && !error && (
          <List sx={{ maxHeight: 400, overflow: 'auto' }}>
            {currentPath !== '/' && (
              <ListItem button onClick={handleUpLevel}>
                <ListItemIcon>
                  <NavigateBeforeIcon />
                </ListItemIcon>
                <ListItemText primary="... (Up one level)" />
              </ListItem>
            )}
            {folders.map((folder) => (
              <ListItem button key={folder.id || folder.name} onClick={() => handleFolderClick(folder)}>
                <ListItemIcon>
                  <FolderIcon />
                </ListItemIcon>
                <ListItemText primary={folder.name} secondary={`Path: ${folder.path}`} />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSelect} variant="contained" disabled={isLoading}>
          Select Current Path
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FolderBrowserDialog; 